"use client";

import { useState } from "react";
import Link from "next/link";
import { 
  Code, 
  BookOpen, 
  Layers, 
  FileText, 
  ArrowRight, 
  ArrowDown, 
  Settings, 
  Star, 
  List, 
  ListCheck 
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function BetaProgramClient() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [previousExperience, setPreviousExperience] = useState(false);
  const [selectedTier, setSelectedTier] = useState("explorer");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !reason) {
      toast.error("Incomplete form", { description: "Please fill in all required fields." });
      return;
    }
    
    toast.success("Application submitted");
    
    // Reset form
    setEmail("");
    setName("");
    setReason("");
    setPreviousExperience(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
      
      {/* Hero Section */}
      <section className="bg-linear-to-br from-purple-200 to-white py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <Badge className="mb-4 bg-purple-300 hover:bg-purple-400">Limited Spots Available</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
            Join the RentWise <span className="text-purple-400">Beta Program</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Help shape the future of rental property reviews while gaining early access to innovative features and exclusive benefits.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <Button 
              size="lg" 
              className="bg-purple-400 hover:bg-purple-500 text-white"
              onClick={() => document.getElementById("apply-now")?.scrollIntoView({ behavior: "smooth" })}
            >
              Apply Now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-purple-400 text-purple-400 hover:bg-purple-100"
              onClick={() => document.getElementById("program-details")?.scrollIntoView({ behavior: "smooth" })}
            >
              Learn More <ArrowDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white px-3 py-1 text-purple-600">100+</Badge>
              <span className="text-gray-700">Current Beta Users</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white px-3 py-1 text-purple-600">15+</Badge>
              <span className="text-gray-700">Exclusive Features</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white px-3 py-1 text-purple-600">Weekly</Badge>
              <span className="text-gray-700">Product Updates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section id="program-details" className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-foreground">Program Details</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              The RentWise Beta Program gives you access to cutting-edge features before they're available to the general public. Help us refine our platform while enjoying exclusive benefits.
            </p>
          </div>
          
          <Tabs defaultValue="benefits" className="w-full mb-12">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="benefits">Benefits</TabsTrigger>
              <TabsTrigger value="expectations">Expectations</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>
            <TabsContent value="benefits" className="mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-purple-400" />
                      Early Access
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Be the first to experience new RentWise features and provide feedback that shapes their development.</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5 text-purple-400" />
                      Direct Input
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Your feedback goes directly to our product team, giving you a voice in our development process.</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Code className="h-5 w-5 text-purple-400" />
                      Beta Community
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Join an exclusive community of beta testers to share insights and connect with like-minded users.</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-purple-400" />
                      Learning Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Gain access to specialized guides, webinars, and resources to maximize your RentWise experience.</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="expectations" className="mt-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">What We Ask From Beta Participants</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <ListCheck className="h-5 w-5 text-purple-400 mt-1" />
                    <div>
                      <p className="font-medium">Active Participation</p>
                      <p className="text-gray-700">Regularly use the platform and test new features as they're released</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ListCheck className="h-5 w-5 text-purple-400 mt-1" />
                    <div>
                      <p className="font-medium">Thoughtful Feedback</p>
                      <p className="text-gray-700">Provide constructive feedback on your experience with beta features</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ListCheck className="h-5 w-5 text-purple-400 mt-1" />
                    <div>
                      <p className="font-medium">Bug Reporting</p>
                      <p className="text-gray-700">Report any issues or bugs encountered during your testing</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ListCheck className="h-5 w-5 text-purple-400 mt-1" />
                    <div>
                      <p className="font-medium">Community Engagement</p>
                      <p className="text-gray-700">Participate in occasional surveys, interviews, or beta community discussions</p>
                    </div>
                  </li>
                </ul>
              </div>
            </TabsContent>
            
            <TabsContent value="timeline" className="mt-6">
              <div className="relative border-l-2 border-purple-400 pl-6 ml-6 space-y-10">
                <div className="relative">
                  <div className="absolute -left-[30px] mt-1.5 h-5 w-5 rounded-full bg-purple-400"></div>
                  <h3 className="text-xl font-semibold text-foreground">Application & Selection</h3>
                  <p className="text-gray-700 mt-2">
                    Submit your application and our team will review it. Selected participants will receive an invitation email within 2 weeks.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[30px] mt-1.5 h-5 w-5 rounded-full bg-purple-400"></div>
                  <h3 className="text-xl font-semibold text-foreground">Onboarding</h3>
                  <p className="text-gray-700 mt-2">
                    Complete the onboarding process, including setting up your beta profile and joining our beta community channels.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[30px] mt-1.5 h-5 w-5 rounded-full bg-purple-400"></div>
                  <h3 className="text-xl font-semibold text-foreground">Active Participation</h3>
                  <p className="text-gray-700 mt-2">
                    Gain access to beta features, provide feedback, and participate in beta-exclusive events and surveys.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[30px] mt-1.5 h-5 w-5 rounded-full bg-purple-400"></div>
                  <h3 className="text-xl font-semibold text-foreground">Feature Graduation</h3>
                  <p className="text-gray-700 mt-2">
                    Witness the features you helped refine go live to all RentWise users, with your contribution acknowledged.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      <Footer />
    </div>
  );
} 